import React from 'react';
import { useNavigate } from 'react-router-dom';
import { BsPlayFill, BsPauseFill } from 'react-icons/bs';
import { useAuth } from '../../contexts/AuthContext';
import { createUserProfile } from '../../firebase';
import { KiddiFitWordmark } from '../Brand';
import AuthModal from '../AuthModal';
import '../LandingPage.css';
import heroVideo from '../../assets/backgrounds/generated-image-5de20b41-2cb3-4d92-85ad-d1d42a46e266.mp4';
import bgImage2 from '../../assets/backgrounds/KidsWorkingOut.jpg';
import useSampleWorkoutAudio from '../../hooks/useSampleWorkoutAudio';

// Hero content variations
const heroVariations = [
  // Current option
  {
    header: 'A coach in your pocket!',
    description:
      'Fun strength & skill workouts for your kids tailored for their sport. No-equipment audio sessions at home. For Ages 6+.',
  },
  // Option 1: Focus on the Problem
  {
    header: "You're busy. They're antsy. We've got this.",
    description:
      "Fun, no-equipment audio workouts to build your child's strength and skills for any sport.",
  },
  {
    header: 'A Personal Trainer for Your Kid (without the high cost).',
    description:
      'Fun, coach-led audio workouts for kids—at home, without equipment.',
  },
  // Option 2: Focus on the Benefit & Feeling
  {
    header: "Unleash Your Kid's Inner Athlete.",
    description:
      'Turns at-home playtime into fun, coach-led workouts that build strength and skills for their sport.',
  },
  {
    header: "More Than Playtime. It's Training Time.",
    description:
      'Our audio workouts help your kids build confidence and skills for the sports they love—right from home.',
  },
  // Option 3: Focus on Simplicity & Practicality
  {
    header: "Kid's Workouts. Coach-Led. No Equipment.",
    description:
      'Engaging, fun strength and skills training for young athletes, all through audio sessions at home.',
  },
  {
    header: 'Simple Workouts for Sporty Kids.',
    description:
      "Get access to fun, coach-led audio sessions designed to improve your child's strength and skills—no special gear needed.",
  },
];

export default function LandingHero({ showScroll = true }) {
  const navigate = useNavigate();
  const { signIn } = useAuth();
  const [loading, setLoading] = React.useState(false);
  const [showAuthModal, setShowAuthModal] = React.useState(false);

  // Randomly select a hero variation on component mount
  const [selectedVariation] = React.useState(() => {
    const randomIndex = Math.floor(Math.random() * heroVariations.length);
    const variation = heroVariations[randomIndex];

    return variation;
  });

  const { isSamplePlaying, handleSampleWorkout } = useSampleWorkoutAudio([
    '/latest-generated-workout-audio/soccer_session_1_intro.mp3',
    '/latest-generated-exercises-audio/fastFeet_intro.mp3',
    '/latest-generated-exercises-audio/fastFeet.mp3',
    '/latest-generated-exercises-audio/highKnees_intro.mp3',
    '/latest-generated-exercises-audio/highKnees.mp3',
    '/latest-generated-exercises-audio/jumpingJacks_intro.mp3',
    '/latest-generated-exercises-audio/jumpingJacks.mp3',
    '/latest-generated-workout-audio/soccer_session_1_conclusion.mp3',
  ]);

  const handleLogin = async (user) => {
    try {
      await createUserProfile(user.uid, {
        displayName: user.displayName || user.email?.split('@')[0],
        email: user.email,
      });
      navigate('/dashboard');
    } catch (error) {
      console.error('Profile creation error:', error);
      // Still navigate to dashboard even if profile creation fails
      navigate('/dashboard');
    }
  };

  const handleShowAuthModal = () => {
    setShowAuthModal(true);
  };

  const handleScrollDown = () => {
    const featuresSection = document.querySelector('.features-section');
    if (featuresSection) featuresSection.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section className='hero-section'>
      <video
        className='hero-video'
        autoPlay
        muted
        loop
        playsInline
        poster={bgImage2}
      >
        <source src={heroVideo} type='video/mp4' />
        Your browser does not support the video tag.
      </video>
      <div className='hero-content'>
        <h2>
          <a
            href='/'
            onClick={(e) => {
              e.preventDefault();
              navigate('/');
            }}
            className='hero-brand-link'
          >
            <KiddiFitWordmark />
          </a>
        </h2>
        <h1>{selectedVariation.header}</h1>
        <p className='hero-description'>{selectedVariation.description}</p>

        <div className='hero-buttons'>
          <button
            className='sample-workout-btn'
            onClick={() => handleSampleWorkout()}
          >
            {isSamplePlaying ? <BsPauseFill /> : <BsPlayFill />}
            {isSamplePlaying ? 'Sample Workout' : 'Sample Workout'}
          </button>

          <button
            className='google-login-btn'
            onClick={handleShowAuthModal}
            disabled={loading}
          >
            {loading ? (
              'Signing in...'
            ) : (
              <>
                <span>👋</span> Join or Login
              </>
            )}
          </button>
        </div>
      </div>

      {showScroll ? (
        <div className='scroll-arrow' onClick={handleScrollDown}>
          <div className='arrow-icon'>↓</div>
          <span className='scroll-text'>Scroll to explore</span>
        </div>
      ) : null}

      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        onSuccess={handleLogin}
        initialMode='login'
      />
    </section>
  );
}
